/**
 * Invoice Create — Manage Columns persistence helpers.
 *
 * Pure functions so Create wizard + offline tests share one path:
 * type change, Computed toggle (keep formula), preset stash, draft snapshot.
 * Stored type is never rewritten to text/number just because it is unknown.
 */

import {
  sanitizeFormula,
  isComputedColumn,
  isSacHsnColumn,
  findHeadcountCalcColumns,
  dedupeSacHsnColumns,
  normalizeColumnType,
  parseFormulaNumber,
} from '@/utils/invoice-calculation-engine'
import {
  resolveColumnType,
  getDefaultColumnType,
  isSystemColumnType,
} from '@/utils/invoice-column-types'

/** Formula text for the ƒ hint — strips a persisted `ƒ` / `f` / `=` prefix. */
export function displayFormula(formula) {
  return sanitizeFormula(formula)
}

export function persistableColumn(col) {
  if (!col || typeof col !== 'object' || !col.key) return null
  if (isSystemColumnType(col.type)) return null
  const type = resolveColumnType(col.type, col.data_type || col.dataType)
  const formula = sanitizeFormula(col.formula)
  const prior = resolveColumnType(col._priorType)
  return {
    key: col.key,
    label: String(col.label != null ? col.label : '').trim() || col.key,
    type,
    required: !!col.required,
    formula,
    visible: col.visible !== false,
    computed: type === 'computed',
    ...(prior && prior !== 'computed' ? { _priorType: prior } : {}),
  }
}

export function persistableColumnConfig(cols) {
  if (!Array.isArray(cols)) return []
  const seen = new Set()
  return cols
    .map(persistableColumn)
    .filter((c) => {
      if (!c || seen.has(c.key)) return false
      seen.add(c.key)
      return true
    })
}

export function persistablePresetColumnStore(store) {
  if (!store || typeof store !== 'object' || Array.isArray(store)) return {}
  const out = {}
  Object.keys(store).forEach((presetId) => {
    const cols = persistableColumnConfig(store[presetId])
    if (cols.length) out[presetId] = cols
  })
  return out
}

export function applyColumnTypeChange(col, newType, {
  defaultFormula = '',
  defaultType = getDefaultColumnType(),
} = {}) {
  if (!col) return col
  const prev = resolveColumnType(col.type, col.data_type || col.dataType)
  const next = resolveColumnType(newType) || defaultType
  const out = { ...col }
  if (next === 'computed' && prev !== 'computed') {
    out._priorType = prev && prev !== 'computed' ? prev : defaultType
    if (!sanitizeFormula(out.formula)) out.formula = defaultFormula || ''
  }
  out.type = next
  out.computed = next === 'computed'
  return out
}

/**
 * Computed ON/OFF. Formula text is kept when turning off so the user can
 * restore it without retyping.
 */
export function toggleColumnComputed(col, checked, {
  defaultFormula = '',
  defaultType = getDefaultColumnType(),
} = {}) {
  if (!col) return col
  const out = { ...col }
  if (checked) {
    if (!isComputedColumn(out)) {
      const prev = resolveColumnType(out.type, out.data_type || out.dataType)
      out._priorType = prev && prev !== 'computed' ? prev : defaultType
    }
    out.type = 'computed'
    out.computed = true
    if (!sanitizeFormula(out.formula)) out.formula = defaultFormula || ''
  } else {
    const prior = resolveColumnType(out._priorType)
    const fallback = (out.key === 'amount' || out.key === 'baseAmount' || out.key === 'unitPrice')
      ? 'currency'
      : (prior && prior !== 'computed' ? prior : defaultType)
    out.type = fallback === 'computed' ? 'currency' : fallback
    out.computed = false
  }
  return out
}

/** SAC dropdown only when stored type says so (or legacy empty type + SAC key). */
export function usesSacSelectWidget(col) {
  if (!col) return false
  const type = normalizeColumnType(col.type)
  if (type) return type === 'sac-select'
  return isSacHsnColumn(col)
}

/**
 * Amount / Base Amount: ₹ input only when the stored type is currency
 * (or empty type on those keys — legacy templates).
 */
export function usesCurrencyAmountWidget(col) {
  if (!col) return false
  if (isComputedColumn(col)) return false
  const type = normalizeColumnType(col.type)
  if (type) return type === 'currency'
  const key = String(col.key || '').toLowerCase()
  return key === 'amount' || key === 'baseamount' || key === 'unitprice' || key === 'rate'
}

export function stashAndTakePresetColumns(store, fromId, toId, currentCols) {
  const next = persistablePresetColumnStore(store)
  const from = fromId == null || fromId === '' ? '' : String(fromId)
  const to = toId == null || toId === '' ? '' : String(toId)
  if (from && Array.isArray(currentCols) && currentCols.length) {
    next[from] = persistableColumnConfig(currentCols)
  }
  const restored = (to && next[to] && next[to].length)
    ? next[to].map((c) => ({ ...c }))
    : null
  return { store: next, restored }
}

/**
 * Same mapping InvoiceCreateManual uses for the live table / preview /
 * draft. Extracted so offline tests hit the real persist + resolve path.
 */
export function resolveCreateValidationColumns(sourceConfig, templateColumns = []) {
  const templateByKey = new Map((templateColumns || []).map((c) => [c.key, c]))
  const source = (sourceConfig && sourceConfig.length > 0)
    ? sourceConfig
    : (templateColumns || [])
  const seenKeys = new Set()
  const cols = source
    .filter((c) => {
      if (!c || isSystemColumnType(c.type)) return false
      if (!c.key || seenKeys.has(c.key)) return false
      seenKeys.add(c.key)
      return true
    })
    .map((c) => {
      const tmpl = templateByKey.get(c.key) || {}
      const label = String(c.label != null ? c.label : (tmpl.label || '')).trim()
        || tmpl.label
        || c.key
      const formula = sanitizeFormula(
        (c.formula != null && c.formula !== '')
          ? c.formula
          : (tmpl.formula || ''),
      )
      let type = resolveColumnType(
        c.type != null && c.type !== '' ? c.type : tmpl.type,
        c.data_type || c.dataType || tmpl.data_type || tmpl.dataType,
      )
      const storedType = normalizeColumnType(c.type)
      // Synonyms (formula/calculated) and formula-only prod columns become
      // computed. An explicit non-computed type still wins (Computed OFF).
      if (isComputedColumn(c) || (storedType === '' && isComputedColumn(tmpl))) {
        type = 'computed'
      } else if (storedType && storedType !== 'computed') {
        type = resolveColumnType(c.type)
      }
      return {
        key: c.key,
        label,
        type,
        required: c.required !== undefined ? !!c.required : !!tmpl.required,
        formula,
        visible: c.visible !== undefined ? c.visible !== false : tmpl.visible !== false,
        computed: type === 'computed',
      }
    })

  const headcountCols = findHeadcountCalcColumns(cols)
  const dynByKey = new Map((sourceConfig || []).map((c) => [c.key, c]))
  if (headcountCols) {
    const amountCol = cols.find((c) => c.key === headcountCols.amountKey)
    if (amountCol) {
      const dyn = dynByKey.get(amountCol.key)
      const userManual = dyn && !isComputedColumn(dyn)
      if (!userManual) {
        amountCol.type = 'computed'
        amountCol.computed = true
      }
    }
    if (headcountCols.monthToDayKey && headcountCols.rateCardKey) {
      const monthToDayCol = cols.find((c) => c.key === headcountCols.monthToDayKey)
      if (monthToDayCol) {
        const dyn = dynByKey.get(monthToDayCol.key)
        const userManual = dyn && !isComputedColumn(dyn)
        if (!userManual) {
          monthToDayCol.type = 'computed'
          monthToDayCol.computed = true
        }
      }
    }
  }
  return dedupeSacHsnColumns(cols)
}

export function buildCustomColumn({
  label,
  type,
  existingKeys = [],
  slugify,
} = {}) {
  const name = String(label || '').trim()
  if (!name) return null
  const key = typeof slugify === 'function' ? slugify(name) : slugifyColumnKey(name, existingKeys)
  const resolved = resolveColumnType(type) || getDefaultColumnType()
  return {
    key,
    label: name,
    type: resolved,
    required: false,
    formula: '',
    visible: true,
    computed: resolved === 'computed',
  }
}

/**
 * Line-item cell validation shared by Create wizard + offline tests.
 * Computed / move / actions are never user-editable.
 */
export function validateInvoiceLineItemCell(col, value) {
  if (!col) return ''
  const type = normalizeColumnType(col.type)
  if (isComputedColumn(col) || type === 'move' || type === 'actions') return ''
  const isNumeric = type === 'number' || type === 'currency' || type === 'percentage'
  const isBlank = value === undefined || value === null || String(value).trim() === ''
  if (isNumeric) {
    if (isBlank) return col.required ? 'Required' : ''
    const num = parseFormulaNumber(value)
    if (num == null) return 'Must be a valid number'
    if (num < 0) return 'Cannot be negative'
    if (col.required && num <= 0) return 'Must be greater than 0'
    return ''
  }
  if (col.required && isBlank) return 'Required'
  return ''
}

export function slugifyColumnKey(label, existingKeys = []) {
  const words = String(label || '').trim().split(/[^a-zA-Z0-9]+/).filter(Boolean)
  const base = words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('') || 'field'
  const existing = new Set(existingKeys)
  if (!existing.has(base)) return base
  let n = 2
  while (existing.has(`${base}${n}`)) n += 1
  return `${base}${n}`
}
